/**
 * @file index.tsx
 *
 * @fileoverview Renders popular blog tags box.
 */
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import BlogTagBadge from '~/jobiqo/components/Blog/components/TagBadge';
import { BlogTag } from '~/jobiqo/models/blog';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Box } from '~/vcl/atoms/07-box';
import { TagsIcon } from '~/vcl/icons/Tags';

interface BlogPopularTagsProps {
  /**
   * The popular tags.
   */
  tags: BlogTag[];
}

interface BlogTagBadgeInterface {
  /**
   * The blog tag.
   */
  blogTag: BlogTag;
  /**
   * The popular blog tag size.
   */
  size: number;
}

const MIN_BADGE_SIZE = 12;
const BADGE_SIZE_INCREMENT = 5;

export default function BlogPopularTags({ tags }: BlogPopularTagsProps) {
  const [blogTagsPerSize, setBlogTagsPerSize] = useState<
    BlogTagBadgeInterface[]
  >([]);
  const intl = useIntl();
  const popularTagsTitle = intl.formatMessage({
    id: 'blog_popular_tags_title',
    defaultMessage: 'Popular tags',
  });

  if (tags.length === 0) {
    return null;
  }

  useEffect(() => {
    // Extract all the amounts from popular tags' count.
    const amountsArray = tags.map(({ count }) => count);
    const maxAmount = Math.max(...amountsArray);
    const minAmount = Math.min(...amountsArray);
    // Find the min-max difference and split it by 3, because
    // we are rendering badges in three different sizes.
    const sumOperator = (maxAmount - minAmount) / 3;
    // Crete three breakpoints that are used to display
    // badges in three different sizes.
    const sizeBreakpoints = [
      minAmount + sumOperator,
      minAmount + 2 * sumOperator,
      maxAmount,
    ];
    const blogTagsPerSize: BlogTagBadgeInterface[] = [];

    tags.forEach(function (popularBlogTag) {
      // For each breakpoint, find tags that have count (number
      // of articles tagged with it) less than the certain breakpoint.
      sizeBreakpoints.forEach((breakpoint, index) => {
        if (
          popularBlogTag.count <= breakpoint &&
          !blogTagsPerSize.find(
            (blogTag) =>
              blogTag.blogTag.term.label === popularBlogTag.term.label,
          )
        ) {
          blogTagsPerSize.push({ size: index, blogTag: popularBlogTag });
        }
      });
    });

    setBlogTagsPerSize(blogTagsPerSize);
  }, [tags]);

  return (
    <Box
      marginTop={0}
      label={
        <FlexItems>
          <FlexItem marginRight={4}>
            <TagsIcon title={popularTagsTitle} color="gray4" />
          </FlexItem>
          {popularTagsTitle}
        </FlexItems>
      }
    >
      <FlexItems gap={3} alignItems="center">
        {blogTagsPerSize.map(({ size, blogTag }) => (
          <FlexItem key={blogTag.term.tid}>
            <BlogTagBadge
              tag={blogTag.term}
              fontSize={`${MIN_BADGE_SIZE + BADGE_SIZE_INCREMENT * size}px`}
              amount={blogTag.count}
              showTagIcon={false}
            />
          </FlexItem>
        ))}
      </FlexItems>
    </Box>
  );
}

export { BlogPopularTags };
